<template>
  <div class="elasticsearch-page header-space">
    <div class="elasticsearch-page__inner">
      <header class="page-header">
        <div class="page-header__text">
          <p class="page-kicker">{{ t('kicker') }}</p>
          <h1>{{ t('title') }}</h1>
          <p class="page-lead">{{ t('intro') }}</p>
        </div>
        <div class="header-actions">
          <div class="language-selector" role="group" :aria-label="t('languageAria')">
            <button
              type="button"
              class="lang-btn"
              :class="{ 'lang-btn--active': lang === 'en' }"
              :aria-pressed="lang === 'en'"
              :aria-label="t('switchToEn')"
              @click="setLang('en')"
            >
              EN
            </button>
            <button
              type="button"
              class="lang-btn"
              :class="{ 'lang-btn--active': lang === 'de' }"
              :aria-pressed="lang === 'de'"
              :aria-label="t('switchToDe')"
              @click="setLang('de')"
            >
              DE
            </button>
          </div>
          <p
            class="status-chip"
            :class="esStatus && esStatus.reachable ? 'status-chip--online' : 'status-chip--offline'"
          >
            <span class="status-chip__dot" aria-hidden="true" />
            {{ esStatus && esStatus.reachable ? t('statusOnline') : t('statusOffline') }}
          </p>
          <span
            v-if="clusterLabel"
            class="status-chip status-chip--cluster"
          >{{ clusterLabel }}</span>
          <span class="host-chip" :title="displayHost">{{ displayHost }}</span>
          <button
            type="button"
            class="admin-btn admin-btn--ghost"
            :disabled="loading"
            @click="refreshAll"
          >
            {{ loading ? t('pleaseWait') : t('refresh') }}
          </button>
        </div>
      </header>

      <aside
        v-if="esStatus && esStatus.error"
        class="callout callout--warning"
        role="status"
      >
        <div class="callout__icon" aria-hidden="true">
          <v-icon small>mdi-alert-outline</v-icon>
        </div>
        <div class="callout__body">
          <p class="callout__title">{{ statusErrorTitle }}</p>
          <p class="callout__text">{{ statusErrorBody }}</p>
        </div>
      </aside>

      <aside
        v-if="deleteIndexResponse"
        class="callout callout--error"
        role="status"
      >
        <div class="callout__body">
          <p class="callout__text">{{ deleteIndexResponse }}</p>
        </div>
      </aside>

      <div class="kpi-grid">
        <div
          v-for="stat in kpiStats"
          :key="stat.label"
          class="kpi-card"
          :class="stat.tone ? 'kpi-card--' + stat.tone : null"
        >
          <p class="kpi-card__label">{{ stat.label }}</p>
          <p class="kpi-card__value">{{ stat.value }}</p>
          <p v-if="stat.hint" class="kpi-card__hint">{{ stat.hint }}</p>
        </div>
      </div>

      <section class="section-card mapping-section">
        <div class="mapping-section__top">
          <div class="mapping-section__copy">
            <h2 class="section-card__title">{{ t('mappingTitle') }}</h2>
            <p class="section-card__lead">{{ t('mappingIntro') }}</p>
          </div>
          <div class="mapping-section__cta">
            <button
              type="button"
              class="admin-btn admin-btn--primary mapping-section__btn"
              :disabled="updatingMapping"
              @click="updateESMappingAsync"
            >
              <v-progress-circular
                v-if="updatingMapping"
                indeterminate
                size="16"
                width="2"
                color="white"
                class="mapping-section__spinner"
                aria-hidden="true"
              ></v-progress-circular>
              {{ updatingMapping ? t('pleaseWait') : t('mappingUpdate') }}
            </button>
          </div>
        </div>

        <aside
          v-if="mappingAlertType"
          class="callout mapping-section__alert"
          :class="'callout--' + mappingAlertType"
          role="status"
        >
          <div class="callout__body">
            <p class="callout__text">{{ esMappingUpdateResponse }}</p>
          </div>
        </aside>

        <div
          v-if="updatingMapping || mappingProgressRows.length"
          class="progress-block"
        >
          <div class="progress-block__head">
            <div class="progress-block__titles">
              <p class="progress-block__label">{{ t('mappingProgressLabel') }}</p>
              <p
                v-if="mappingPhaseHint"
                class="progress-block__phase"
              >{{ mappingPhaseHint }}</p>
            </div>
            <p class="progress-block__pct">{{ mappingProgressPercent }}%</p>
          </div>
          <v-progress-linear
            :value="mappingProgressPercent"
            :indeterminate="
              updatingMapping &&
                mappingProgressPercent === 0 &&
                !mappingProgressRows.length
            "
            height="10"
            rounded
            color="primary"
            background-color="#e8eef5"
          ></v-progress-linear>

          <div v-if="mappingProgressRows.length" class="progress-tiles">
            <div
              v-for="row in mappingProgressRows"
              :key="row.label"
              class="progress-tile"
              :class="{ 'progress-tile--done': row.completed }"
            >
              <div class="progress-tile__head">
                <p class="progress-tile__name">{{ row.label }}</p>
                <span class="progress-tile__pct">{{ row.percent }}%</span>
              </div>
              <v-progress-linear
                :value="row.percent"
                height="6"
                rounded
                :color="row.completed ? 'success' : 'primary'"
                background-color="#e8eef5"
              ></v-progress-linear>
              <p class="progress-tile__meta">{{ row.detail }}</p>
            </div>
          </div>
        </div>
      </section>

      <section class="section-card">
        <h2 class="section-card__title">{{ t('indicesTitle') }}</h2>
        <p class="section-card__lead">{{ t('indicesIntro') }}</p>

        <div class="data-panel">
          <div class="data-panel__head">
            <h3>{{ t('indicesPanelTitle') }}</h3>
            <p v-if="orphanIndices.length || brokenIndices.length" class="data-panel__hint">
              <span v-if="brokenIndices.length">{{
                t('indicesBrokenCount', { n: brokenIndices.length })
              }}</span>
              <span v-if="brokenIndices.length && orphanIndices.length"> · </span>
              <span v-if="orphanIndices.length">{{
                orphanIndices.length === 1
                  ? t('indicesOrphanCount', { n: orphanIndices.length })
                  : t('indicesOrphanCountPlural', { n: orphanIndices.length })
              }}</span>
            </p>
          </div>
          <div class="data-panel__body">
            <div v-if="loading" class="data-panel__empty-state">
              <p class="data-panel__empty">{{ t('indicesLoading') }}</p>
            </div>
            <div
              v-else-if="!indexInventory.length"
              class="data-panel__empty-state"
            >
              <p class="data-panel__empty-title">{{ t('indicesEmptyTitle') }}</p>
              <p class="data-panel__empty">{{ t('indicesEmpty') }}</p>
            </div>
            <template v-else>
              <table class="system-table system-table--desktop">
                <thead>
                  <tr>
                    <th class="col-name">{{ t('colIndex') }}</th>
                    <th>{{ t('colStatus') }}</th>
                    <th>{{ t('colMapping') }}</th>
                    <th>{{ t('colAliases') }}</th>
                    <th>{{ t('colDocs') }}</th>
                    <th>{{ t('colSize') }}</th>
                    <th>{{ t('colAction') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="idx in indexInventory"
                    :key="'t-' + idx.index"
                    :class="{
                      'es-row-orphan': idx.isOrphan,
                      'es-row-broken': idx.isBroken
                    }"
                  >
                    <td class="col-name">
                      <code class="mono">{{ idx.index }}</code>
                    </td>
                    <td>
                      <span
                        class="status-chip"
                        :class="indexStatusClass(idx)"
                      >
                        <span class="status-chip__dot" aria-hidden="true" />
                        {{ indexStatusLabel(idx) }}
                      </span>
                      <span
                        v-if="idx.isBroken"
                        class="status-chip status-chip--broken"
                      >
                        <span class="status-chip__dot" aria-hidden="true" />
                        {{ t('statusBroken') }}
                      </span>
                    </td>
                    <td>
                      <span
                        v-if="idx.family === 'award'"
                        class="mapping-cell"
                        :class="{ 'mapping-cell--bad': idx.isBroken }"
                        :title="mappingIssuesText(idx)"
                      >
                        {{
                          idx.isBroken
                            ? mappingIssuesText(idx)
                            : t('mappingOk')
                        }}
                      </span>
                      <span v-else class="muted">—</span>
                    </td>
                    <td>
                      <span v-if="idx.aliases && idx.aliases.length">{{
                        idx.aliases.join(', ')
                      }}</span>
                      <span v-else class="muted">—</span>
                    </td>
                    <td>{{ idx.docs != null ? idx.docs : '—' }}</td>
                    <td>{{ idx.storeSize || '—' }}</td>
                    <td>
                      <button
                        v-if="idx.isOrphan"
                        type="button"
                        class="sys-link-btn sys-link-btn--danger"
                        :disabled="deletingIndex"
                        @click="openDeleteDialog(idx)"
                      >
                        {{ t('delete') }}
                      </button>
                      <span v-else class="muted">{{ t('protected') }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <ul class="record-list" :aria-label="t('indicesPanelTitle')">
                <li
                  v-for="idx in indexInventory"
                  :key="'c-' + idx.index"
                  class="record-card"
                  :class="{
                    'record-card--orphan': idx.isOrphan,
                    'record-card--broken': idx.isBroken
                  }"
                >
                  <div class="record-card__top">
                    <p class="record-card__title mono">{{ idx.index }}</p>
                    <div class="record-card__badges">
                      <span
                        class="status-chip"
                        :class="indexStatusClass(idx)"
                      >
                        <span class="status-chip__dot" aria-hidden="true" />
                        {{ indexStatusLabel(idx) }}
                      </span>
                      <span
                        v-if="idx.isBroken"
                        class="status-chip status-chip--broken"
                      >
                        <span class="status-chip__dot" aria-hidden="true" />
                        {{ t('statusBroken') }}
                      </span>
                    </div>
                  </div>
                  <dl class="record-card__meta">
                    <div v-if="idx.family === 'award'" class="record-card__field">
                      <dt>{{ t('colMapping') }}</dt>
                      <dd :class="{ 'mapping-cell--bad': idx.isBroken }">
                        {{
                          idx.isBroken
                            ? mappingIssuesText(idx)
                            : t('mappingOk')
                        }}
                      </dd>
                    </div>
                    <div class="record-card__field">
                      <dt>{{ t('colAliases') }}</dt>
                      <dd>
                        <template v-if="idx.aliases && idx.aliases.length">{{
                          idx.aliases.join(', ')
                        }}</template>
                        <span v-else class="muted">—</span>
                      </dd>
                    </div>
                    <div class="record-card__field">
                      <dt>{{ t('colDocs') }}</dt>
                      <dd>{{ idx.docs != null ? idx.docs : '—' }}</dd>
                    </div>
                    <div class="record-card__field">
                      <dt>{{ t('colSize') }}</dt>
                      <dd>{{ idx.storeSize || '—' }}</dd>
                    </div>
                  </dl>
                  <div class="record-card__action">
                    <button
                      v-if="idx.isOrphan"
                      type="button"
                      class="admin-btn admin-btn--danger"
                      :disabled="deletingIndex"
                      @click="openDeleteDialog(idx)"
                    >
                      {{ t('delete') }}
                    </button>
                    <span v-else class="muted">{{ t('protected') }}</span>
                  </div>
                </li>
              </ul>
            </template>
          </div>
        </div>
      </section>

      <section class="section-card">
        <h2 class="section-card__title">{{ t('syncTitle') }}</h2>
        <p class="section-card__lead">{{ t('syncIntro') }}</p>

        <div class="data-panel" style="margin-bottom: 1rem">
          <div class="data-panel__head">
            <h3>{{ t('syncQueueTitle') }}</h3>
          </div>
          <div class="data-panel__body">
            <ul v-if="syncRequestedRows.length" class="sync-list">
              <li
                v-for="row in syncRequestedRows"
                :key="row.name"
                class="sync-list__item"
              >
                <span>{{ row.name }}</span>
                <span>{{ row.count }}</span>
              </li>
              <li class="sync-list__item sync-list__item--total">
                <span>{{ t('syncTotal') }}</span>
                <span>{{ (esStatus && esStatus.syncRequestedTotal) || 0 }}</span>
              </li>
            </ul>
            <div v-else class="data-panel__empty-state">
              <p class="data-panel__empty">{{ t('syncQueueEmpty') }}</p>
            </div>
          </div>
        </div>

        <div class="action-row" style="margin-bottom: 0.75rem">
          <button
            type="button"
            class="admin-btn admin-btn--ghost"
            :disabled="loadingBlocked"
            @click="getBlockedList"
          >
            {{ loadingBlocked ? t('pleaseWait') : t('blockedReload') }}
          </button>
          <button
            type="button"
            class="admin-btn admin-btn--danger"
            :disabled="blockedTotal === 0 || clearingAll"
            @click="confirmClearDialog = true"
          >
            {{ t('clearAllSyncRequests') }}
          </button>
        </div>

        <div class="data-panel">
          <div class="data-panel__head">
            <h3>{{ t('blockedTitle') }}</h3>
            <p class="data-panel__hint">
              {{ t('blockedCount', { n: blockedTotal }) }}
              <span v-if="blockedEntities.length < blockedTotal">
                — {{ t('blockedTruncated', { n: blockedEntities.length }) }}
              </span>
            </p>
          </div>
          <div class="data-panel__body">
            <div v-if="loadingBlocked" class="data-panel__empty-state">
              <p class="data-panel__empty">{{ t('blockedLoading') }}</p>
            </div>
            <div
              v-else-if="blockedEntities.length === 0"
              class="data-panel__empty-state"
            >
              <p class="data-panel__empty-title">{{ t('blockedEmptyTitle') }}</p>
              <p class="data-panel__empty">{{ t('blockedEmpty') }}</p>
            </div>
            <template v-else>
              <table class="system-table system-table--desktop">
                <thead>
                  <tr>
                    <th class="col-name">{{ t('colName') }}</th>
                    <th>{{ t('colId') }}</th>
                    <th>{{ t('colType') }}</th>
                    <th>{{ t('colShortCode') }}</th>
                    <th>{{ t('colSyncRequested') }}</th>
                    <th>{{ t('colAction') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="entity in blockedEntities"
                    :key="'t-' + entity.type + '-' + entity.id"
                  >
                    <td class="col-name">
                      <a
                        :href="getDetailHref(entity)"
                        target="_blank"
                        rel="noopener noreferrer"
                      >{{ entity.name }}</a>
                    </td>
                    <td>
                      <a
                        class="mono entity-id-link"
                        :href="getDetailHref(entity)"
                        target="_blank"
                        rel="noopener noreferrer"
                      >{{ entity.id }}</a>
                    </td>
                    <td>{{ entity.type }}</td>
                    <td>{{ entity.shortCode || '—' }}</td>
                    <td>{{ formatDate(entity.elasticSyncRequestedAt || entity.blockedAt) }}</td>
                    <td>
                      <button
                        type="button"
                        class="sys-link-btn"
                        @click="clearEntitySyncRequest(entity)"
                      >
                        {{ t('clearSyncRequest') }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <ul class="record-list" :aria-label="t('blockedTitle')">
                <li
                  v-for="entity in blockedEntities"
                  :key="'c-' + entity.type + '-' + entity.id"
                  class="record-card"
                >
                  <div class="record-card__top">
                    <div>
                      <p class="record-card__title">
                        <a
                          :href="getDetailHref(entity)"
                          target="_blank"
                          rel="noopener noreferrer"
                        >{{ entity.name }}</a>
                      </p>
                      <p class="record-card__id">
                        <a
                          class="mono entity-id-link"
                          :href="getDetailHref(entity)"
                          target="_blank"
                          rel="noopener noreferrer"
                        >{{ entity.id }}</a>
                      </p>
                    </div>
                  </div>
                  <dl class="record-card__meta">
                    <div class="record-card__field">
                      <dt>{{ t('colType') }}</dt>
                      <dd>{{ entity.type }}</dd>
                    </div>
                    <div class="record-card__field">
                      <dt>{{ t('colShortCode') }}</dt>
                      <dd>{{ entity.shortCode || '—' }}</dd>
                    </div>
                    <div class="record-card__field">
                      <dt>{{ t('colSyncRequested') }}</dt>
                      <dd>{{ formatDate(entity.elasticSyncRequestedAt || entity.blockedAt) }}</dd>
                    </div>
                  </dl>
                  <div class="record-card__action">
                    <button
                      type="button"
                      class="admin-btn admin-btn--ghost"
                      @click="clearEntitySyncRequest(entity)"
                    >
                      {{ t('clearSyncRequest') }}
                    </button>
                  </div>
                </li>
              </ul>
            </template>
          </div>
        </div>
        <p v-if="clearAllResponse" class="muted" style="margin: 0.75rem 0 0">
          {{ clearAllResponse }}
        </p>
      </section>

      <AdminConfirmDialog
        v-model="confirmDeleteDialog"
        :title="t('deleteConfirmTitle')"
        :cancel-label="t('cancel')"
        :confirm-label="t('deleteConfirm')"
        :busy-label="t('pleaseWait')"
        :busy="deletingIndex"
        confirm-variant="danger"
        @confirm="confirmDeleteIndex"
      >
        <p style="margin: 0 0 0.5rem">{{ t('deleteConfirmLead') }}</p>
        <code class="mono">{{ pendingDeleteIndex }}</code>
        <p class="muted" style="margin: 0.75rem 0 0; font-size: 0.8125rem">
          {{ t('deleteConfirmHint') }}
        </p>
      </AdminConfirmDialog>

      <AdminConfirmDialog
        v-model="confirmClearDialog"
        :title="t('clearAllSyncConfirmTitle')"
        :body="t('clearAllSyncConfirmBody')"
        :cancel-label="t('cancel')"
        :confirm-label="t('clearAllSyncRequests')"
        :busy-label="t('pleaseWait')"
        :busy="clearingAll"
        confirm-variant="danger"
        @confirm="clearAllSyncRequests"
      />
    </div>
  </div>
</template>

<script>
import AdminConfirmDialog from '@/components/AdminConfirmDialog.vue'
import { elasticsearchT } from '@/i18n/elasticsearch'
import { readStoredLang, writeStoredLang } from '@/i18n/adminLang'

const LANG_STORAGE_KEY = 'fledw-elasticsearch-lang'

export default {
  name: 'Elasticsearch',
  components: { AdminConfirmDialog },
  data: () => ({
    lang: readStoredLang(LANG_STORAGE_KEY),
    esMappingUpdateResponse: null,
    deleteIndexResponse: null,
    esStatus: null,
    blockedEntities: [],
    blockedTotal: 0,
    pendingDeleteIndex: null,
    confirmDeleteDialog: false,
    loading: false,
    loadingBlocked: false,
    deletingIndex: false,
    updatingMapping: false,
    mappingPollTimer: null,
    mappingJob: null,
    mappingProgressPercent: 0,
    mappingProgressRows: [],
    clearingAll: false,
    confirmClearDialog: false,
    clearAllResponse: null
  }),
  computed: {
    mappingAlertType: function () {
      const msg = this.esMappingUpdateResponse
      if (!msg) return null
      if (
        String(msg).indexOf('Failed') === 0 ||
        String(msg).indexOf('Fehlgeschlagen') === 0 ||
        String(msg).indexOf('Reindex error') === 0 ||
        String(msg).indexOf('Reindex-Fehler') === 0
      ) {
        return 'error'
      }
      if (
        String(msg).indexOf('Done') === 0 ||
        String(msg).indexOf('Fertig') === 0
      ) {
        return 'success'
      }
      // Live progress lives in the progress block — avoid a second cramped banner.
      return null
    },
    mappingPhaseHint: function () {
      if (!this.updatingMapping) return null
      const msg = this.esMappingUpdateResponse
      if (!msg) return this.t('mappingStarting')
      if (
        String(msg).indexOf('Finalizing') === 0 ||
        String(msg).indexOf('Aliase werden') === 0
      ) {
        return this.t('mappingFinalizing')
      }
      if (
        String(msg).indexOf('Starting') === 0 ||
        String(msg).indexOf('Starting…') === 0 ||
        String(msg) === this.t('mappingStarting')
      ) {
        return this.t('mappingStarting')
      }
      return this.t('mappingKeepOpen')
    },
    indexInventory: function () {
      return (this.esStatus && this.esStatus.indices) || []
    },
    orphanIndices: function () {
      return this.indexInventory.filter((i) => i.isOrphan)
    },
    brokenIndices: function () {
      return this.indexInventory.filter((i) => i.isBroken)
    },
    clusterLabel: function () {
      const key = (this.esStatus && this.esStatus.cluster) || ''
      const map = {
        local: 'clusterLocal',
        stage: 'clusterStage',
        prod: 'clusterProd',
        unknown: 'clusterUnknown'
      }
      return map[key] ? this.t(map[key]) : (key ? String(key) : '')
    },
    statusErrorTitle: function () {
      const err = (this.esStatus && this.esStatus.error) || ''
      if (this.isForbiddenError(err)) return this.t('warningForbiddenTitle')
      return this.shortEsError(err)
    },
    statusErrorBody: function () {
      const err = (this.esStatus && this.esStatus.error) || ''
      if (this.isForbiddenError(err)) return this.t('warningForbiddenBody')
      return this.t('warningEmptyBody')
    },
    displayHost: function () {
      const host = (this.esStatus && this.esStatus.host) || ''
      return host.replace(/\/\/[^@]+@/, '//***@') || '—'
    },
    syncRequestedRows: function () {
      const map = (this.esStatus && this.esStatus.syncRequested) || {}
      return Object.keys(map).map((name) => ({
        name,
        count: map[name]
      }))
    },
    kpiStats: function () {
      const c = (this.esStatus && this.esStatus.counts) || {}
      const broken = this.brokenIndices.length
      const queued = (this.esStatus && this.esStatus.syncRequestedTotal) || 0
      return [
        {
          label: this.t('kpiAwards'),
          value: this.formatCount(c.awards),
          hint: this.t('kpiDocsHint'),
          tone: null
        },
        {
          label: this.t('kpiReleases'),
          value: this.formatCount(c.releases),
          hint: this.t('kpiDocsHint'),
          tone: null
        },
        {
          label: this.t('kpiDynamicFields'),
          value: this.formatCount(c.dynamic_fields),
          hint: this.t('kpiDocsHint'),
          tone: null
        },
        {
          label: this.t('kpiBroken'),
          value: this.formatCount(broken),
          hint:
            broken > 0 ? this.t('kpiBrokenHint') : this.t('kpiBrokenOk'),
          tone: broken > 0 ? 'danger' : 'ok'
        },
        {
          label: this.t('kpiSyncQueued'),
          value: this.formatCount(queued),
          hint: this.t('kpiSyncHint'),
          tone: queued > 0 ? 'accent' : null
        }
      ]
    }
  },
  methods: {
    t (key, vars) {
      return elasticsearchT(this.lang, key, vars)
    },
    setLang (nextLang) {
      if (this.lang === nextLang) return
      this.lang = nextLang
      writeStoredLang(LANG_STORAGE_KEY, nextLang)
    },
    indexStatusClass (idx) {
      if (idx.isLive) return 'status-chip--live'
      if (idx.isOrphan) return 'status-chip--orphan'
      return 'status-chip--other'
    },
    indexStatusLabel (idx) {
      if (idx.isLive) return this.t('statusLive')
      if (idx.isOrphan) return this.t('statusOrphan')
      return this.t('statusOther')
    },
    mappingIssuesText (idx) {
      const issues = (idx && idx.mappingIssues) || []
      return issues.length ? issues.join('; ') : this.t('statusBroken')
    },
    refreshAll: async function () {
      await Promise.allSettled([this.getList(), this.getBlockedList()])
    },
    getDetailHref: function (entity) {
      if (!entity || !entity.type || !entity.id) return '#'
      return this.$router.resolve({
        path: '/' + entity.type.toLowerCase() + '/' + entity.id
      }).href
    },
    formatCount: function (value) {
      const n = this.normalizeCount(value)
      if (n === null) return '—'
      return n.toLocaleString(this.lang === 'de' ? 'de-DE' : 'en-US')
    },
    normalizeCount: function (value) {
      if (value === null || value === undefined || value === '') return null
      if (typeof value === 'number' && Number.isFinite(value)) return value
      // Defensive: older API returned the full ES count payload.
      if (typeof value === 'object' && value !== null && 'count' in value) {
        return this.normalizeCount(value.count)
      }
      const n = Number(value)
      return Number.isFinite(n) ? n : null
    },
    shortEsError: function (message) {
      if (!message) return ''
      const text = String(message)
      if (this.isForbiddenError(text)) {
        return this.t('warningForbiddenTitle')
      }
      if (text.indexOf('no such index') !== -1) {
        return this.t('warningEmptyTitle')
      }
      return text.length > 220 ? text.slice(0, 220) + '…' : text
    },
    isForbiddenError: function (message) {
      const text = String(message || '')
      return (
        text.indexOf('not authorized') !== -1 ||
        text.indexOf('ESHttpGet') !== -1 ||
        text.indexOf('403') !== -1 ||
        text.indexOf('security_exception') !== -1
      )
    },
    getList: async function () {
      this.loading = true
      try {
        const response = await this.$http.get('/elasticsearch/status')
        if (Array.isArray(response.data)) {
          this.esStatus = {
            host: '',
            reachable: false,
            error: null,
            indices: [],
            counts: {},
            syncRequested: {},
            syncRequestedTotal: 0
          }
        } else {
          this.esStatus = response.data
        }
      } finally {
        this.loading = false
      }
    },
    getBlockedList: async function () {
      this.loadingBlocked = true
      try {
        const response = await this.$http.get('/sync-blocked/list')
        this.blockedEntities = response.data.items
        this.blockedTotal = response.data.total
      } finally {
        this.loadingBlocked = false
      }
    },
    openDeleteDialog: function (idx) {
      this.pendingDeleteIndex = idx.index
      this.confirmDeleteDialog = true
    },
    confirmDeleteIndex: async function () {
      const indexName = this.pendingDeleteIndex
      if (!indexName) return
      this.deletingIndex = true
      this.deleteIndexResponse = null
      try {
        await this.$http.delete('/elasticsearch/delete/' + indexName)
        this.confirmDeleteDialog = false
        this.pendingDeleteIndex = null
        await this.getList()
      } catch (e) {
        this.deleteIndexResponse = this.t('deleteFailed', {
          error: (e && e.response && e.response.data) || e.message || e
        })
      } finally {
        this.deletingIndex = false
      }
    },
    updateESMappingAsync: async function () {
      this.updatingMapping = true
      this.mappingProgressPercent = 0
      this.mappingProgressRows = []
      this.esMappingUpdateResponse = this.t('mappingStarting')
      try {
        const start = await this.$http.post('/elasticsearch/apply-mapping/start')
        this.mappingJob = start.data
        const taskIds = (this.mappingJob.tasks || []).map((t) => t.taskId)
        this.esMappingUpdateResponse = this.t('mappingReindexRunning', {
          index:
            (this.mappingJob.newIndices && this.mappingJob.newIndices.award) ||
            ''
        })
        await this.pollMappingUntilDone(taskIds)
      } catch (e) {
        this.esMappingUpdateResponse = this.t('mappingFailed', {
          error: (e && e.response && e.response.data) || e.message || e
        })
        this.updatingMapping = false
        this.mappingProgressPercent = 0
        this.mappingProgressRows = []
      }
    },
    taskLabelForId: function (taskId) {
      const tasks = (this.mappingJob && this.mappingJob.tasks) || []
      const match = tasks.find((t) => t.taskId === taskId)
      return (match && match.label) || taskId
    },
    computeTaskProgress: function (task) {
      const p = task.progress || {}
      const total = Number(p.total) || 0
      const done =
        (Number(p.created) || 0) +
        (Number(p.updated) || 0) +
        (Number(p.deleted) || 0) +
        (Number(p.noops) || 0)
      let percent = 0
      if (task.completed && !task.error) {
        percent = 100
      } else if (total > 0) {
        percent = Math.min(100, Math.round((done / total) * 100))
      }
      return {
        label: this.taskLabelForId(task.taskId),
        percent,
        completed: !!task.completed,
        detail:
          total > 0
            ? this.t('mappingDocs', { done, total })
            : task.completed
              ? this.t('mappingFinished')
              : this.t('mappingWaitingEs')
      }
    },
    pollMappingUntilDone: async function (taskIds) {
      const pollOnce = async () => {
        const statusRes = await this.$http.post(
          '/elasticsearch/apply-mapping/status',
          { taskIds }
        )
        const status = statusRes.data
        const rows = (status.tasks || []).map((t) => this.computeTaskProgress(t))
        this.mappingProgressRows = rows
        if (rows.length > 0) {
          const sum = rows.reduce((acc, r) => acc + r.percent, 0)
          this.mappingProgressPercent = Math.round(sum / rows.length)
        }
        this.esMappingUpdateResponse = rows
          .map((r) => r.label + ': ' + r.percent + '% (' + r.detail + ')')
          .join(' · ')

        if (status.hasErrors) {
          this.updatingMapping = false
          this.esMappingUpdateResponse = this.t('mappingReindexError')
          await this.getList()
          return
        }

        if (status.allCompleted) {
          this.mappingProgressPercent = 100
          this.esMappingUpdateResponse = this.t('mappingFinalizing')
          await this.$http.post('/elasticsearch/apply-mapping/finalize', {
            oldIndices: this.mappingJob.oldIndices,
            newIndices: this.mappingJob.newIndices
          })
          this.esMappingUpdateResponse = this.t('mappingDone', {
            indices: Object.values(this.mappingJob.newIndices || {}).join(', ')
          })
          this.updatingMapping = false
          this.mappingJob = null
          this.mappingProgressRows = this.mappingProgressRows.map((r) =>
            Object.assign({}, r, {
              percent: 100,
              completed: true,
              detail: this.t('mappingFinished')
            })
          )
          await this.getList()
          return
        }

        this.mappingPollTimer = setTimeout(pollOnce, 5000)
      }

      await pollOnce()
    },
    clearEntitySyncRequest: async function (entity) {
      await this.$http.put(
        '/sync-blocked/' + entity.type + '/' + entity.id + '/clear-sync-request'
      )
      // Refetch instead of splicing locally, so a capped list pulls in the next eligible row.
      await this.refreshAll()
    },
    clearAllSyncRequests: async function () {
      this.clearingAll = true
      try {
        const response = await this.$http.delete('/sync-blocked/clear-sync-requests')
        this.clearAllResponse = this.t('clearedSyncRequests', {
          n: response.data.cleared
        })
        this.blockedEntities = []
        this.blockedTotal = 0
        this.confirmClearDialog = false
        await this.getList()
      } finally {
        this.clearingAll = false
      }
    },
    formatDate: function (value) {
      if (!value) return '—'
      return new Date(value).toLocaleString()
    }
  },
  async created () {
    await this.$auth.waitForAuth()
    if (!this.$auth.isAuthenticated) {
      await this.$router.push('/')
      return
    }
    await this.refreshAll()
  },
  beforeDestroy () {
    if (this.mappingPollTimer) {
      clearTimeout(this.mappingPollTimer)
      this.mappingPollTimer = null
    }
  }
}
</script>
